/**
 * AIRA ECHO - Tours & Experiences Page
 * Powered by Viator Affiliate API
 * 
 * Features:
 * - Destination search
 * - Free text search
 * - Filter by category, price, duration, rating
 * - Product details modal
 * - Affiliate redirect to Viator for booking
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, MapPin, Calendar, Clock, Star, Heart, 
  Filter, X, ChevronRight, Loader2, Globe, Sparkles,
  Shield, Ticket, Users, DollarSign, ArrowRight,
  CheckCircle, Mountain, Camera, Utensils, Palmtree,
  Landmark, Ship, Bike, Music, Sunset, Coffee
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTheme } from '../context/ThemeContext';
import { FloatingBackButton } from '../components/layout/FloatingBackButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Destination hero images
const DESTINATION_IMAGES = {
  'dubai': 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1920&q=80',
  'paris': 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=1920&q=80',
  'london': 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=1920&q=80',
  'new york': 'https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?w=1920&q=80',
  'singapore': 'https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=1920&q=80',
  'bali': 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=1920&q=80',
  'rome': 'https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=1920&q=80',
  'tokyo': 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=1920&q=80',
  'bangkok': 'https://images.unsplash.com/photo-1508009603885-50cf7c579365?w=1920&q=80',
  'default': 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=1920&q=80'
};

// Category icons
const CATEGORY_ICONS = {
  'adventure': Mountain,
  'sightseeing': Camera,
  'food': Utensils,
  'beach': Palmtree,
  'cultural': Landmark,
  'cruise': Ship,
  'outdoor': Bike,
  'nightlife': Music,
  'sunset': Sunset,
  'tours': Globe
};

// Currency symbols
const CURRENCY_SYMBOLS = {
  'USD': '$', 'EUR': '€', 'GBP': '£', 'INR': '₹',
  'AED': 'AED ', 'SGD': 'S$', 'AUD': 'A$', 'JPY': '¥'
};

// Popular destinations
const POPULAR_DESTINATIONS = [
  { name: 'Dubai', country: 'UAE', emoji: '🇦🇪' },
  { name: 'Paris', country: 'France', emoji: '🇫🇷' },
  { name: 'London', country: 'UK', emoji: '🇬🇧' },
  { name: 'Singapore', country: 'Singapore', emoji: '🇸🇬' },
  { name: 'Bali', country: 'Indonesia', emoji: '🇮🇩' },
  { name: 'Rome', country: 'Italy', emoji: '🇮🇹' },
  { name: 'Tokyo', country: 'Japan', emoji: '🇯🇵' },
  { name: 'Bangkok', country: 'Thailand', emoji: '🇹🇭' },
  { name: 'New York', country: 'USA', emoji: '🇺🇸' },
  { name: 'Barcelona', country: 'Spain', emoji: '🇪🇸' },
  { name: 'Delhi', country: 'India', emoji: '🇮🇳' },
  { name: 'Goa', country: 'India', emoji: '🇮🇳' },
];

// Tour Card Component
const TourCard = ({ tour, onSelect, currency }) => {
  const { isDark } = useTheme();
  
  const primaryImage = tour.images?.[0]?.url || 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=800&q=80';
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -5 }}
      className={`relative rounded-2xl overflow-hidden cursor-pointer group ${
        isDark 
          ? 'bg-white/5 border border-white/10' 
          : 'bg-white border border-gray-200 shadow-lg'
      }`}
      onClick={() => onSelect(tour)}
      data-testid={`tour-card-${tour.code}`}
    >
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <img 
          src={primaryImage}
          alt={tour.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        
        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-wrap gap-2">
          {tour.free_cancellation && (
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-emerald-500/90 text-white flex items-center gap-1">
              <Shield className="w-3 h-3" />
              Free Cancellation
            </span>
          )}
          {tour.special_offer && (
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-500/90 text-white">
              Special Offer
            </span>
          )}
        </div>
        
        {/* Rating */}
        {tour.rating && (
          <div className="absolute bottom-3 left-3 flex items-center gap-1 bg-black/60 backdrop-blur-sm px-2 py-1 rounded-lg">
            <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
            <span className="text-white font-medium text-sm">{tour.rating.toFixed(1)}</span>
            {tour.review_count > 0 && (
              <span className="text-white/60 text-xs">({tour.review_count.toLocaleString()})</span>
            )}
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="p-4">
        {/* Destination */}
        {tour.destination && (
          <div className={`flex items-center gap-1 text-xs mb-2 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
            <MapPin className="w-3 h-3" />
            {tour.destination}
          </div>
        )}
        
        {/* Title */}
        <h3 className={`font-semibold text-base mb-2 line-clamp-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {tour.title}
        </h3>
        
        {/* Duration */}
        {tour.duration_text && (
          <div className={`flex items-center gap-1 text-sm mb-3 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
            <Clock className="w-4 h-4" />
            {tour.duration_text}
          </div>
        )}
        
        {/* Tags */}
        {tour.tags && tour.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {tour.tags.slice(0, 2).map((tag, idx) => (
              <span 
                key={idx}
                className={`px-2 py-0.5 text-xs rounded-full ${
                  isDark ? 'bg-white/10 text-white/70' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {tag}
              </span>
            ))}
          </div>
        )}
        
        {/* Price */}
        <div className="flex items-center justify-between">
          <div>
            <span className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>From</span>
            <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {tour.price?.formatted || 'Price on request'}
            </p>
          </div>
          <button 
            className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#FF007F] to-[#7B2FBE] text-white text-sm font-medium hover:shadow-lg hover:shadow-pink-500/30 transition-all flex items-center gap-1"
            onClick={(e) => {
              e.stopPropagation();
              onSelect(tour);
            }}
          >
            View <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// Product Details Modal
const ProductDetailsModal = ({ product, currency, onClose }) => {
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  
  useEffect(() => {
    if (product?.code) {
      fetchDetails();
    }
  }, [product]);
  
  const fetchDetails = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/tours/product/${product.code}?currency=${currency}`
      );
      if (response.data.success) {
        setDetails(response.data.product);
      }
    } catch (error) {
      console.error('Failed to fetch product details:', error);
      toast.error('Failed to load tour details');
    } finally {
      setLoading(false);
    }
  };
  
  const handleBookNow = () => {
    const bookingUrl = details?.booking_url || product?.booking_url;
    if (bookingUrl) {
      window.open(bookingUrl, '_blank', 'noopener,noreferrer');
    }
  };
  
  if (!product) return null;
  
  const displayData = details || product;
  const images = displayData.images || [];
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      
      {/* Modal */}
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className={`relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl ${
          isDark ? 'bg-[#0D0B14]' : 'bg-white'
        }`}
        onClick={e => e.stopPropagation()}
        data-testid="tour-details-modal"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        
        {loading ? (
          <div className="flex items-center justify-center h-96">
            <Loader2 className="w-10 h-10 text-[#FF007F] animate-spin" />
          </div>
        ) : (
          <>
            {/* Image Gallery */}
            <div className="relative h-72 md:h-96">
              {images.length > 0 ? (
                <>
                  <img
                    src={images[activeImageIndex]?.url}
                    alt={displayData.title}
                    className="w-full h-full object-cover"
                  />
                  {images.length > 1 && (
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                      {images.slice(0, 5).map((_, idx) => (
                        <button
                          key={idx}
                          onClick={() => setActiveImageIndex(idx)}
                          className={`w-2 h-2 rounded-full transition-all ${
                            idx === activeImageIndex 
                              ? 'bg-white w-6' 
                              : 'bg-white/50 hover:bg-white/70'
                          }`}
                        />
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-[#FF007F]/20 to-[#7B2FBE]/20 flex items-center justify-center">
                  <Globe className="w-20 h-20 text-white/30" />
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            </div>
            
            {/* Content */}
            <div className="p-6 md:p-8">
              {/* Header */}
              <div className="mb-6">
                {displayData.destination?.destinationName && (
                  <div className={`flex items-center gap-1 text-sm mb-2 ${isDark ? 'text-[#FF007F]' : 'text-pink-600'}`}>
                    <MapPin className="w-4 h-4" />
                    {displayData.destination.destinationName}
                  </div>
                )}
                
                <h2 className={`text-2xl md:text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {displayData.title}
                </h2>
                
                {/* Meta Info */}
                <div className="flex flex-wrap items-center gap-4 text-sm">
                  {displayData.rating && (
                    <div className="flex items-center gap-1">
                      <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                      <span className={isDark ? 'text-white' : 'text-gray-900'}>{displayData.rating.toFixed(1)}</span>
                      {displayData.review_count > 0 && (
                        <span className={isDark ? 'text-white/50' : 'text-gray-500'}>
                          ({displayData.review_count.toLocaleString()} reviews)
                        </span>
                      )}
                    </div>
                  )}
                  
                  {displayData.duration_text && (
                    <div className={`flex items-center gap-1 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                      <Clock className="w-4 h-4" />
                      {displayData.duration_text}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Flags */}
              <div className="flex flex-wrap gap-2 mb-6">
                {displayData.flags?.free_cancellation && (
                  <span className="px-3 py-1 text-sm rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center gap-1">
                    <Shield className="w-4 h-4" />
                    Free Cancellation
                  </span>
                )}
                {displayData.flags?.skip_the_line && (
                  <span className="px-3 py-1 text-sm rounded-full bg-blue-500/20 text-blue-400 border border-blue-500/30 flex items-center gap-1">
                    <Ticket className="w-4 h-4" />
                    Skip the Line
                  </span>
                )}
                {displayData.flags?.private_tour && (
                  <span className="px-3 py-1 text-sm rounded-full bg-purple-500/20 text-purple-400 border border-purple-500/30 flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    Private Tour
                  </span>
                )}
                {displayData.flags?.mobile_ticket && (
                  <span className="px-3 py-1 text-sm rounded-full bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                    Mobile Ticket
                  </span>
                )}
              </div>
              
              {/* Description */}
              <div className="mb-6">
                <h3 className={`text-lg font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  About This Experience
                </h3>
                <p className={`leading-relaxed ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                  {displayData.description}
                </p>
              </div>
              
              {/* Highlights */}
              {displayData.highlights && displayData.highlights.length > 0 && (
                <div className="mb-6">
                  <h3 className={`text-lg font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Highlights
                  </h3>
                  <ul className="space-y-2">
                    {displayData.highlights.map((highlight, idx) => (
                      <li key={idx} className={`flex items-start gap-2 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                        <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                        <span>{typeof highlight === 'string' ? highlight : (highlight.description || highlight.typeDescription || JSON.stringify(highlight))}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Inclusions */}
              {displayData.inclusions && displayData.inclusions.length > 0 && (
                <div className="mb-6">
                  <h3 className={`text-lg font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    What's Included
                  </h3>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {displayData.inclusions.map((item, idx) => {
                      const text = typeof item === 'string' ? item : (item.description || item.typeDescription || item.otherDescription || 'Included');
                      return (
                        <li key={idx} className={`flex items-center gap-2 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                          <CheckCircle className="w-4 h-4 text-emerald-400" />
                          <span className="text-sm">{text}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              
              {/* Exclusions */}
              {displayData.exclusions && displayData.exclusions.length > 0 && (
                <div className="mb-6">
                  <h3 className={`text-lg font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Not Included
                  </h3>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {displayData.exclusions.map((item, idx) => {
                      const text = typeof item === 'string' ? item : (item.description || item.typeDescription || item.otherDescription || 'Not included');
                      return (
                        <li key={idx} className={`flex items-center gap-2 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                          <X className="w-4 h-4 text-red-400" />
                          <span className="text-sm">{text}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              
              {/* Cancellation Policy */}
              {displayData.cancellation_policy && (
                <div className={`p-4 rounded-xl mb-6 ${isDark ? 'bg-white/5 border border-white/10' : 'bg-gray-50 border border-gray-200'}`}>
                  <h3 className={`text-sm font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Cancellation Policy
                  </h3>
                  <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                    {typeof displayData.cancellation_policy === 'string' 
                      ? displayData.cancellation_policy 
                      : (displayData.cancellation_policy.description || 
                         (displayData.cancellation_policy.refundable 
                           ? 'Free cancellation available' 
                           : 'Non-refundable'))}
                  </p>
                </div>
              )}
              
              {/* Price & Book */}
              <div className={`sticky bottom-0 p-4 -mx-8 -mb-8 ${isDark ? 'bg-[#0D0B14]/95' : 'bg-white/95'} backdrop-blur-lg border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <span className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>From</span>
                    <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {displayData.price?.formatted || 'Price on request'}
                    </p>
                    <span className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>per person</span>
                  </div>
                  <button
                    onClick={handleBookNow}
                    className="px-8 py-3 rounded-xl bg-gradient-to-r from-[#FF007F] to-[#7B2FBE] text-white font-semibold hover:shadow-lg hover:shadow-pink-500/30 transition-all flex items-center gap-2"
                    data-testid="book-on-viator-btn"
                  >
                    Book on Viator
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
                <p className={`text-xs mt-2 text-center ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                  Powered by Viator • Secure booking • Instant confirmation
                </p>
              </div>
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
};

// Main Tours Page Component
export default function ToursPage() {
  const { isDark } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDestination, setSelectedDestination] = useState('');
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [selectedTour, setSelectedTour] = useState(null);
  const [currency, setCurrency] = useState('USD');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalResults, setTotalResults] = useState(0);
  const [heroImage, setHeroImage] = useState(DESTINATION_IMAGES.default);
  
  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    freeCancellation: false,
    priceFrom: '',
    priceTo: '',
    rating: '',
    sortBy: 'RECOMMENDED'
  });
  
  const searchInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  
  // Handle URL parameters for destination pre-fill
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const destParam = urlParams.get('destination');
    if (destParam) {
      setSearchQuery(destParam);
      setSelectedDestination(destParam);
      // Trigger search after component mounts
      setTimeout(() => {
        handleSearch(destParam, true);
      }, 100);
    }
  }, []);
  
  // Update hero image based on search
  useEffect(() => {
    const destLower = (selectedDestination || searchQuery).toLowerCase();
    const matched = Object.keys(DESTINATION_IMAGES).find(key => destLower.includes(key));
    setHeroImage(matched ? DESTINATION_IMAGES[matched] : DESTINATION_IMAGES.default);
  }, [selectedDestination, searchQuery]);
  
  // Debounced search
  const handleSearch = useCallback(async (query = searchQuery, isNewSearch = true) => {
    if (!query.trim() && !selectedDestination) {
      toast.error('Please enter a destination or search term');
      return;
    }
    
    if (isNewSearch) {
      setPage(1);
      setTours([]);
    }
    
    setLoading(true);
    setSearched(true);
    
    try {
      const params = {
        q: query.trim() || selectedDestination,
        currency,
        page: isNewSearch ? 1 : page,
        page_size: 20,
        ...(filters.freeCancellation && { free_cancellation: true }),
        ...(filters.priceFrom && { price_from: parseFloat(filters.priceFrom) }),
        ...(filters.priceTo && { price_to: parseFloat(filters.priceTo) }),
        ...(filters.rating && { rating: parseFloat(filters.rating) }),
        sort_by: filters.sortBy
      };
      
      const response = await axios.get(`${API_URL}/api/tours/freetext`, { params });
      
      if (response.data.success) {
        const newTours = response.data.products || [];
        setTours(isNewSearch ? newTours : [...tours, ...newTours]);
        setTotalResults(response.data.total || 0);
        setHasMore(newTours.length === 20);
        
        if (isNewSearch && newTours.length > 0) {
          toast.success(`Found ${response.data.total || newTours.length} experiences!`);
        } else if (isNewSearch && newTours.length === 0) {
          toast.info('No experiences found. Try a different search.');
        }
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Failed to search experiences');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedDestination, currency, filters, page, tours]);
  
  const handleDestinationClick = (destination) => {
    setSelectedDestination(destination.name);
    setSearchQuery(destination.name);
    handleSearch(destination.name, true);
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch(searchQuery, true);
    }
  };
  
  const loadMore = () => {
    setPage(prev => prev + 1);
    handleSearch(searchQuery, false);
  };
  
  const applyFilters = () => {
    setShowFilters(false);
    handleSearch(searchQuery, true);
  };
  
  const resetFilters = () => {
    setFilters({
      freeCancellation: false,
      priceFrom: '',
      priceTo: '',
      rating: '',
      sortBy: 'RECOMMENDED'
    });
  };
  
  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#09080E]' : 'bg-gray-50'}`} data-testid="tours-page">
      <FloatingBackButton />
      
      {/* ==================== HERO SECTION ==================== */}
      <section className="relative h-[70vh] min-h-[500px] overflow-hidden">
        <motion.div
          key={heroImage}
          initial={{ scale: 1.1, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.5 }}
          className="absolute inset-0"
        >
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${heroImage})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#09080E]/70 via-transparent to-[#09080E]" />
        </motion.div>
        
        {/* Hero Content */}
        <div className="relative z-10 h-full flex flex-col justify-end pb-12 px-4 md:px-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-8"
          >
            <p className="text-[#FF007F] text-sm uppercase tracking-[0.3em] mb-4 flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              AIRA Tours & Experiences
            </p>
            <h1 className="font-['Playfair_Display'] text-4xl md:text-6xl text-white font-bold leading-tight max-w-4xl">
              Discover Unforgettable <br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF007F] via-[#00E5FF] to-[#FF007F]">
                Experiences
              </span>
            </h1>
            <p className="text-white/60 text-lg mt-4 max-w-xl">
              Explore tours, activities, and attractions worldwide. Book with confidence.
            </p>
          </motion.div>
          
          {/* Search Widget */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className={`${isDark ? 'bg-black/40' : 'bg-white/90'} backdrop-blur-2xl border ${isDark ? 'border-white/10' : 'border-gray-200'} rounded-2xl p-6 shadow-2xl max-w-4xl`}
          >
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search Input */}
              <div className="flex-1 relative">
                <Search className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-white/40' : 'text-gray-400'}`} />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Search tours, activities, attractions..."
                  className={`w-full pl-12 pr-4 py-3 rounded-xl ${
                    isDark 
                      ? 'bg-white/5 border border-white/10 text-white placeholder-white/30' 
                      : 'bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400'
                  } focus:border-[#FF007F] focus:ring-1 focus:ring-[#FF007F] transition-all`}
                  data-testid="tour-search-input"
                />
              </div>
              
              {/* Currency Selector */}
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className={`px-4 py-3 rounded-xl ${
                  isDark 
                    ? 'bg-white/5 border border-white/10 text-white' 
                    : 'bg-gray-50 border border-gray-200 text-gray-900'
                } cursor-pointer`}
                data-testid="currency-select"
              >
                <option value="USD">🇺🇸 USD</option>
                <option value="EUR">🇪🇺 EUR</option>
                <option value="GBP">🇬🇧 GBP</option>
                <option value="INR">🇮🇳 INR</option>
                <option value="AED">🇦🇪 AED</option>
                <option value="SGD">🇸🇬 SGD</option>
              </select>
              
              {/* Filter Button */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-4 py-3 rounded-xl border ${
                  showFilters || Object.values(filters).some(v => v && v !== 'RECOMMENDED')
                    ? 'bg-[#FF007F]/20 border-[#FF007F] text-[#FF007F]'
                    : isDark 
                      ? 'bg-white/5 border-white/10 text-white hover:bg-white/10' 
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                } transition-all flex items-center gap-2`}
                data-testid="filter-btn"
              >
                <Filter className="w-5 h-5" />
                Filters
              </button>
              
              {/* Search Button */}
              <button
                onClick={() => handleSearch(searchQuery, true)}
                disabled={loading}
                className="px-8 py-3 rounded-xl bg-gradient-to-r from-[#FF007F] to-[#7B2FBE] text-white font-semibold hover:shadow-lg hover:shadow-pink-500/30 transition-all flex items-center gap-2 disabled:opacity-50"
                data-testid="search-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
                Search
              </button>
            </div>
            
            {/* Filters Panel */}
            <AnimatePresence>
              {showFilters && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className={`mt-4 pt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      {/* Free Cancellation */}
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={filters.freeCancellation}
                          onChange={(e) => setFilters({...filters, freeCancellation: e.target.checked})}
                          className="w-4 h-4 rounded border-gray-300 text-[#FF007F] focus:ring-[#FF007F]"
                        />
                        <span className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                          Free Cancellation
                        </span>
                      </label>
                      
                      {/* Price Range */}
                      <input
                        type="number"
                        placeholder="Min Price"
                        value={filters.priceFrom}
                        onChange={(e) => setFilters({...filters, priceFrom: e.target.value})}
                        className={`px-3 py-2 rounded-lg text-sm ${
                          isDark 
                            ? 'bg-white/5 border border-white/10 text-white placeholder-white/30' 
                            : 'bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400'
                        }`}
                      />
                      <input
                        type="number"
                        placeholder="Max Price"
                        value={filters.priceTo}
                        onChange={(e) => setFilters({...filters, priceTo: e.target.value})}
                        className={`px-3 py-2 rounded-lg text-sm ${
                          isDark 
                            ? 'bg-white/5 border border-white/10 text-white placeholder-white/30' 
                            : 'bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400'
                        }`}
                      />
                      
                      {/* Rating */}
                      <select
                        value={filters.rating}
                        onChange={(e) => setFilters({...filters, rating: e.target.value})}
                        className={`px-3 py-2 rounded-lg text-sm ${
                          isDark 
                            ? 'bg-white/5 border border-white/10 text-white' 
                            : 'bg-gray-50 border border-gray-200 text-gray-900'
                        }`}
                      >
                        <option value="">Any Rating</option>
                        <option value="4">4+ Stars</option>
                        <option value="4.5">4.5+ Stars</option>
                      </select>
                      
                      {/* Sort By */}
                      <select
                        value={filters.sortBy}
                        onChange={(e) => setFilters({...filters, sortBy: e.target.value})}
                        className={`px-3 py-2 rounded-lg text-sm ${
                          isDark 
                            ? 'bg-white/5 border border-white/10 text-white' 
                            : 'bg-gray-50 border border-gray-200 text-gray-900'
                        }`}
                      >
                        <option value="RECOMMENDED">Recommended</option>
                        <option value="PRICE_LOW_TO_HIGH">Price: Low to High</option>
                        <option value="PRICE_HIGH_TO_LOW">Price: High to Low</option>
                        <option value="TRAVELER_RATING">Highest Rated</option>
                      </select>
                    </div>
                    
                    <div className="flex justify-end gap-2 mt-4">
                      <button
                        onClick={resetFilters}
                        className={`px-4 py-2 rounded-lg text-sm ${
                          isDark ? 'text-white/60 hover:text-white' : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        Reset
                      </button>
                      <button
                        onClick={applyFilters}
                        className="px-4 py-2 rounded-lg bg-[#FF007F] text-white text-sm font-medium hover:bg-[#FF007F]/80 transition-colors"
                      >
                        Apply Filters
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            
            {/* Popular Destinations */}
            <div className="mt-6 pt-4 border-t border-white/10">
              <p className={`text-sm mb-3 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Popular destinations</p>
              <div className="flex flex-wrap gap-2">
                {POPULAR_DESTINATIONS.slice(0, 8).map((dest) => (
                  <button
                    key={dest.name}
                    onClick={() => handleDestinationClick(dest)}
                    className={`px-4 py-2 rounded-full text-sm ${
                      isDark 
                        ? 'bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 hover:text-white' 
                        : 'bg-gray-100 border border-gray-200 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
                    } transition-all flex items-center gap-1`}
                  >
                    <span>{dest.emoji}</span>
                    {dest.name}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* ==================== SEARCH RESULTS ==================== */}
      <AnimatePresence>
        {searched && (
          <motion.section
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="px-4 md:px-16 py-16"
          >
            {loading && tours.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20">
                <Loader2 className="w-12 h-12 text-[#FF007F] animate-spin mb-4" />
                <p className={isDark ? 'text-white/60' : 'text-gray-500'}>
                  Searching for amazing experiences...
                </p>
              </div>
            ) : tours.length > 0 ? (
              <>
                {/* Results Header */}
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {totalResults.toLocaleString()} Experiences Found
                    </h2>
                    <p className={`text-sm mt-1 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                      {searchQuery || selectedDestination}
                    </p>
                  </div>
                </div>
                
                {/* Results Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {tours.map((tour) => (
                    <TourCard
                      key={tour.code}
                      tour={tour}
                      onSelect={setSelectedTour}
                      currency={currency}
                    />
                  ))}
                </div>
                
                {/* Load More */}
                {hasMore && (
                  <div className="flex justify-center mt-12">
                    <button
                      onClick={loadMore}
                      disabled={loading}
                      className={`px-8 py-3 rounded-xl ${
                        isDark 
                          ? 'bg-white/5 border border-white/10 text-white hover:bg-white/10' 
                          : 'bg-gray-100 border border-gray-200 text-gray-700 hover:bg-gray-200'
                      } transition-all flex items-center gap-2 disabled:opacity-50`}
                      data-testid="load-more-btn"
                    >
                      {loading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          Load More
                          <ChevronRight className="w-5 h-5" />
                        </>
                      )}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-20">
                <Globe className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-white/20' : 'text-gray-300'}`} />
                <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  No Experiences Found
                </h3>
                <p className={isDark ? 'text-white/50' : 'text-gray-500'}>
                  Try a different destination or search term
                </p>
              </div>
            )}
          </motion.section>
        )}
      </AnimatePresence>
      
      {/* ==================== DESTINATIONS GRID (Pre-Search) ==================== */}
      {!searched && (
        <section className="px-4 md:px-16 py-16">
          <div className="mb-10">
            <p className="text-[#FF007F] text-sm uppercase tracking-[0.3em] mb-3">
              Explore
            </p>
            <h2 className={`font-['Playfair_Display'] text-3xl md:text-4xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Popular Destinations
            </h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {POPULAR_DESTINATIONS.map((dest, index) => (
              <motion.button
                key={dest.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => handleDestinationClick(dest)}
                className={`relative h-40 rounded-2xl overflow-hidden group ${
                  isDark ? '' : 'shadow-lg'
                }`}
                data-testid={`destination-${dest.name.toLowerCase().replace(' ', '-')}`}
              >
                <div 
                  className="absolute inset-0 bg-cover bg-center group-hover:scale-110 transition-transform duration-700"
                  style={{ 
                    backgroundImage: `url(${DESTINATION_IMAGES[dest.name.toLowerCase()] || DESTINATION_IMAGES.default})` 
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <p className="text-white font-bold text-lg">{dest.name}</p>
                  <p className="text-white/60 text-sm">{dest.country}</p>
                </div>
                <div className="absolute top-3 right-3 text-2xl">{dest.emoji}</div>
              </motion.button>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== VIATOR BRANDING ==================== */}
      <section className={`px-4 md:px-16 py-8 ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
        <div className="flex items-center justify-center gap-2">
          <span className={`text-sm ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
            Powered by
          </span>
          <span className={`font-semibold ${isDark ? 'text-white/60' : 'text-gray-700'}`}>
            Viator
          </span>
          <span className={`text-sm ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
            • Secure booking • Instant confirmation
          </span>
        </div>
      </section>
      
      {/* ==================== PRODUCT DETAILS MODAL ==================== */}
      <AnimatePresence>
        {selectedTour && (
          <ProductDetailsModal
            product={selectedTour}
            currency={currency}
            onClose={() => setSelectedTour(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
